import { decimalSeparatedPrice } from "@/apiUtils/commonUtils";
import { ConfiguratorStepProps, LensTypeData, TranslationData } from "@/common/lib/types";
import Button from "@/components/ui/button/Button";
import Divider from "@/components/ui/divider/Divider";
import { cn } from "@/lib/tailwind";
import Image from "next/image";
import RenderPrice from "@/components/productPrice/RenderPrice";
import AddToCart from "../../components/AddToCart";
import { lensTypes } from "../../resource/config";
import NoImageSrc from "@/assets/images/no_image.svg";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { useConfiguratorContext } from "../../context/Provider";
import { getAttributeValue, formatDiscountPercentLabel } from "@/apiUtils/productUtils";
import { getPromotionBadgeNameFromPromotions } from "@/utils/promotionUtils";
import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";

interface LensOptionProps {
  lang: string;
  className?: string;
  onSelect: () => void;
  currencyCode: string;
  lensData: LensTypeData;
  translations: TranslationData;
  onAddToCart: ConfiguratorStepProps["handleAddToCart"];
  onCloseLensConfigurator?: () => void;
}

const LensTypeCard = ({
  lang,
  onSelect,
  lensData,
  className,
  onAddToCart,
  onCloseLensConfigurator,
  translations,
  currencyCode,
}: LensOptionProps) => {
  const {
    productInfo
  } = useConfiguratorContext();
  const lensPrice = decimalSeparatedPrice(parseFloat(lensData?.price));
  const undiscountprice = decimalSeparatedPrice(parseFloat(lensData?.undiscountprice));
  const isPairEyewear = productInfo?.brand === "Pair Eyewear";
  const brand = productInfo?.brand;
  const model = getAttributeValue(productInfo?.attributes, "model") as string;
  const offerLabel = formatDiscountPercentLabel(lensData.discountPercent);
  const promoBadgeName = getPromotionBadgeNameFromPromotions(lensData.availablePromotions, lang);
  const hasOffer = String(lensData.price) !== String(lensData.undiscountprice);

  return (
    <div
      className={cn(
        "col-span-full flex h-full flex-col bg-tertiary-3 lg:col-span-4 lg:max-w-[246px]",
        className,
      )}
    >
      <div className="flex gap-4 px-4 pt-3 md:px-0 md:pt-0 lg:flex-col">
        <Image
          width={246}
          height={144}
          src={lensData.media ? getCloudinaryURL(lensData.media) : NoImageSrc}
          className="h-[59px] w-20 lg:h-[144px] lg:w-full"
          alt={lensData.title[lang] + " banner"}
        />
        <div className="sm:px-0 lg:px-3 ">
          <p className="text-[0.875rem] font-semibold">{lensData.title[lang]}</p>
          <p className="text-[0.75rem] text-tertiary-6">{lensData.subtitle}</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col px-3 py-4">
        <Divider containerClassName="-mx-3" />

        <div className="flex-1 py-4">
          <ul className="list-outside list-disc pl-4">
            {lensData.features.map((feature, idx) => (
              <li className="text-[0.813rem]" key={idx}>
                {feature.title}
              </li>
            ))}
          </ul>
        </div>

        <Divider containerClassName="-mx-3" />

        <div
          className={cn(
            "mt-4 flex items-center justify-between gap-2 ",
            lensData?.type === lensTypes.FRAME_ONLY && "justify-end",
          )}
        >
          {lensData?.type === lensTypes.FRAME_ONLY ? (
            <></>
          ) : (
            <div className="flex flex-col">
              {undiscountprice !== lensPrice ? (
                <>
                  <p className="text-[0.813rem] font-bold text-gray-500 line-through">
                    {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[0]}
                    <RenderPrice price={undiscountprice} currencyCode={currencyCode} lang={lang} />
                    {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[1]}
                  </p>
                  <p className="text-[0.75rem] font-bold text-red-500">
                    {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[0]}
                    <RenderPrice price={lensPrice} currencyCode={currencyCode} lang={lang} />
                    {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[1]}
                  </p>
                </>
              ) : (
                <p className="text-[0.813rem] font-bold">
                  {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[0]}
                  <RenderPrice price={lensPrice} currencyCode={currencyCode} lang={lang} />
                  {translations?.lensConfigurator?.lens_config_from_price?.split("{{price}}")[1]}
                </p>
              )}
            </div>
          )}

          {lensData?.type === lensTypes.FRAME_ONLY ? (
            <AddToCart
              handleAddToCart={onAddToCart}
              onCloseLensConfigurator={onCloseLensConfigurator}
              translations={translations}
              isPairEyewear={isPairEyewear}
              isBundle={productInfo?.isBundle}
              model={model}
              brand={brand!}
              variantId={productInfo?.variantId}
              pairEyewearTopsContent={productInfo?.pairEyewearTopsContent}
            >
              {translations?.lensConfigurator?.lens_config_select_button_label ?? ""}
            </AddToCart>
          ) : (
            <div className="flex items-end h-full">
              <Button onClick={onSelect} className="px-3 py-1 sm:px-3 sm:py-1 md:px-3 md:py-1">
                {translations?.lensConfigurator?.lens_config_select_button_label || "Select"}
              </Button>
            </div>
          )}
        </div>
        {/* TODO(QA): When Qatar (qa) is enabled, gate offerLabel % with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
            via getPercentHideCountries()/displayPercentage (same as Magrabi LensTypeStep). */}
        {lensData?.type !== lensTypes.FRAME_ONLY && hasOffer && (
          <div className="mt-1 text-sm font-semibold">{offerLabel}</div>
        )}
        {lensData?.type !== lensTypes.FRAME_ONLY && promoBadgeName && hasOffer ? (
          <p className="mt-1 text-sm font-semibold text-secondary-1">
            <RenderCurrencyInText text={promoBadgeName} />
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default LensTypeCard;